import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mePreferencesApi } from "@/api/mePreferencesApi";
import { preferenceKeys } from "./preferenceKeys";

/**
 * One typed per-user preference, read through the server and written back
 * optimistically.
 *
 * <p>`value` is the parsed preference, or `fallback` while it loads, when it
 * has never been set, or when what is stored cannot be parsed — a preference
 * written by an older build must never break the page that reads it, so the
 * parse is wrapped and a bad blob simply reads as unset. `isLoading` is
 * exposed so a host that keys a QUERY on the value can wait for the real
 * answer rather than firing once with the fallback and again with the
 * preference.</p>
 *
 * <p>`set` writes the cache first so the UI moves on the click, then persists.
 * The mutation invalidates the key on settle, so a failed write falls back to
 * what the server actually holds rather than leaving the optimistic value
 * standing.</p>
 */
export function useUserPreference<T>(
  key: string,
  fallback: T,
): { value: T; set: (next: T) => void; isLoading: boolean } {
  const qc = useQueryClient();
  const queryKey = preferenceKeys.key(key);

  const query = useQuery({
    queryKey,
    queryFn: () => mePreferencesApi.get(key),
    // A preference is a small string that changes only when this user changes
    // it; there is nothing to gain from refetching it on every mount.
    staleTime: Infinity,
  });

  const mutation = useMutation({
    mutationFn: (next: T) => mePreferencesApi.put(key, JSON.stringify(next)),
    onMutate: (next: T) => {
      qc.setQueryData(queryKey, JSON.stringify(next));
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });

  const set = useCallback((next: T) => mutation.mutate(next), [mutation]);

  let value = fallback;
  if (typeof query.data === "string") {
    try {
      value = JSON.parse(query.data) as T;
    } catch {
      value = fallback;
    }
  }

  return { value, set, isLoading: query.isLoading };
}
